import React from "react";

// Fallback "logo" for billers that don't ship one (e.g. Nomba betting providers):
// the name's initials on a colour derived from the name, so a provider always
// gets the same colour. Every colour keeps white text at ≥4.5:1 contrast.
const AVATAR_COLORS = [
  "#0B75C2",
  "#7C3AED",
  "#DB2777",
  "#C2410C",
  "#15803D",
  "#0E7490",
  "#B45309",
  "#B91C1C",
  "#4338CA",
  "#047857",
];

export const getInitials = (name: string): string => {
  const words = name
    .replace(/([a-z])([A-Z])/g, "$1 $2") // "SportyBet" → "Sporty Bet"
    .replace(/([a-zA-Z])(\d)|(\d)([a-zA-Z])/g, "$1$3 $2$4") // "1960Bet" → "1960 Bet"
    .split(/[^a-zA-Z0-9]+/) // spaces, brackets, dashes, etc.
    .filter(Boolean);
  const initials =
    words.length >= 2
      ? words[0][0] + words[1][0]
      : (words[0] ?? "").slice(0, 2);
  return initials.toUpperCase() || "?";
};

const getColor = (name: string): string => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = (hash * 31 + name.charCodeAt(i)) | 0;
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

type ProviderAvatarProps = {
  name: string;
  logoUrl?: string | null;
  size?: number;
};

const ProviderAvatar: React.FC<ProviderAvatarProps> = ({
  name,
  logoUrl,
  size = 32,
}) => {
  const [logoFailed, setLogoFailed] = React.useState(false);

  if (logoUrl && !logoFailed) {
    return (
      <img
        src={logoUrl}
        alt={name}
        onError={() => setLogoFailed(true)}
        style={{ width: size, height: size }}
        className="shrink-0 rounded-full bg-white object-contain"
      />
    );
  }

  return (
    <div
      aria-hidden
      style={{
        width: size,
        height: size,
        backgroundColor: getColor(name),
        fontSize: Math.round(size * 0.38),
      }}
      className="flex shrink-0 select-none items-center justify-center rounded-full font-sora font-bold leading-none text-white"
    >
      {getInitials(name)}
    </div>
  );
};

export default ProviderAvatar;
